import {z} from 'zod';
import {easeConfigSchema} from './easing';
import {secondsOrMsSchema, type SecondsOrMs} from '../shared';
import {playbackSettingsSchema} from './playback';

type AlignOperator = '<' | '>';
type PlusOrMinus = '+' | '-';
type AbsolutePosition = SecondsOrMs;
type RelativeOperator = `${PlusOrMinus}=${number}`;
type AlignOperatorWithOffset = `${AlignOperator}${RelativeOperator}`;

export const timelinePositionSchema = z.union([
  // Absolute position (number or duration string) - clamp negative values to 0
  z.union([
    z.number().transform((val) => (val < 0 ? 0 : val)),
    z.string().regex(/^\d+(\.\d+)?ms$/, {
      message: 'Absolute position must be a number or milliseconds string',
    }),
  ]),

  // Alignment operators
  z.enum(['<', '>']),

  // Relative operator pattern: "+=0.5" or "-=2"
  z.string().regex(/^[+-]=\d+(\.\d+)?$/, {
    message: 'Relative operator must be in format "+=1.5" or "-=0.5"',
  }),

  // Alignment with offset: "<+=0.5" or ">-=1"
  z.string().regex(/^[<>][+-]=\d+(\.\d+)?$/, {
    message: 'Alignment with offset must be in format "<+=1.5" or ">-=0.5"',
  }),
]) as z.ZodType<
  AbsolutePosition | AlignOperator | AlignOperatorWithOffset | RelativeOperator
>;
export type TimelinePosition = z.infer<typeof timelinePositionSchema>;

export const staggerConfigSchema = z.object({
  /** Time offset between each animation start (in seconds) */
  amount: secondsOrMsSchema.optional(),
  /** Axis to stagger along ('x' or 'y') */
  axis: z.enum(['x', 'y']).optional(),
  /** Time in seconds between each animation (alternative to amount) */
  each: secondsOrMsSchema.optional(),
  /** Easing function to apply to the stagger offsets */
  ease: easeConfigSchema.optional(),
  /** Starting point for the stagger sequence */
  from: z
    .union([
      z.enum(['start', 'center', 'end', 'edges', 'random']),
      z.number(),
      // Accept string representations of numbers (coerce to number)
      z.string().transform((val, ctx) => {
        const num = Number(val);
        if (isNaN(num)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'String must be a valid number',
          });
          return z.NEVER;
        }
        return num;
      }),
      z.null(),
    ])
    .optional(),
  /** Grid configuration for 2D staggering */
  grid: z
    .union([z.literal('auto'), z.tuple([z.number(), z.number()]), z.null()])
    .optional(),
});
export type StaggerConfig = z.infer<typeof staggerConfigSchema>;

export const timingConfigSchema = z
  .object({
    /** Delay before the animation starts (in seconds) */
    delay: z.number().nonnegative().optional(),
    /** Duration of the animation (in seconds) */
    duration: secondsOrMsSchema.optional(),
    /** Number of times to repeat the animation (-1 for infinite) */
    repeat: z.number().int().optional(),
    /** Delay between repetitions (in seconds) */
    repeatDelay: secondsOrMsSchema.optional(),
    /** Position within the timeline (absolute, relative, or alignment-based) */
    position: timelinePositionSchema.optional(),
    /** Whether to play the animation forward then backward on repeat */
    yoyo: z.boolean().optional(),
    /** Configuration for staggered animations across multiple targets */
    stagger: staggerConfigSchema.optional(),
    /** Easing function to use */
    ease: easeConfigSchema.optional(),
    /** Whether the animation should automatically reverse when complete */
    autoReverse: z.boolean().optional(),
  })
  .merge(playbackSettingsSchema);

export type TimingConfig = z.infer<typeof timingConfigSchema>;
